import { useRouter } from 'next/router';

import {
  JOURNEY_TYPE,
  RAIL_EUROPE_TRAIN_CATEGORY_TYPE,
  TRAIN_PLACE_SPLIT_SYMBOL,
} from '~constants/pointToPointTicket';

import type {
  SearchPlaceInformation,
  SearchPlaceParams,
  TravelerInfo,
} from '~models/Transportation/PointToPointTicket';
import type { JourneyType } from '~types/queryParam';

interface NavigationOptions {
  replaceHistory?: boolean;
}

const useRailEuropeNavigation = () => {
  const router = useRouter();

  // 검색 페이지로 이동하는 함수
  const navigateToSearch = ({
    isSingleTrip,
    origin,
    destination,
    departureDate,
    arrivalDate,
    ages,
    outboundDepartureTime,
    inboundDepartureTime,
    replaceHistory = false,
  }: {
    isSingleTrip: boolean;
    origin?: SearchPlaceInformation;
    destination?: SearchPlaceInformation;
    departureDate?: string;
    arrivalDate?: string;
    ages?: Partial<TravelerInfo>[];
    outboundDepartureTime?: string;
    inboundDepartureTime?: string;
  } & NavigationOptions) => {
    router[replaceHistory ? 'replace' : 'push']({
      pathname: `/search/train/europe`,
      query: {
        category: RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET,
        journey: isSingleTrip ? JOURNEY_TYPE.ONE_WAY : JOURNEY_TYPE.ROUND_TRIP,
        ...(origin?.code &&
          origin?.label &&
          origin?.type && {
            origin: `${origin.label}${TRAIN_PLACE_SPLIT_SYMBOL}${origin.type}${TRAIN_PLACE_SPLIT_SYMBOL}${origin.code}`,
          }),
        ...(destination?.code &&
          destination?.label &&
          destination?.type && {
            destination: `${destination.label}${TRAIN_PLACE_SPLIT_SYMBOL}${destination.type}${TRAIN_PLACE_SPLIT_SYMBOL}${destination.code}`,
          }),
        ...(departureDate && { departureDate }),
        ...(isSingleTrip ? {} : arrivalDate && { arrivalDate }),
        ...(ages && ages.length > 0 && { ages: ages.map(({ age }) => age).join(',') }),
        ...(outboundDepartureTime && { outboundDepartureTime }),
        ...(inboundDepartureTime && { inboundDepartureTime }),
      },
    });
  };

  // 검색 결과 페이지로 이동하는 함수
  const navigateToSearchResult = ({
    correlationId,
    journey,
    origin,
    destination,
    departureDate,
    arrivalDate,
    ages,
    outboundDepartureTime,
    inboundDepartureTime,
    outboundOfferId,
    replaceHistory = false,
  }: {
    correlationId: string;
    journey: JourneyType;
    origin?: SearchPlaceInformation;
    destination?: SearchPlaceInformation;
    departureDate?: string;
    arrivalDate?: string;
    /** 숫자와 콤마가 포함된 문자열: ex) '2,5,20' */
    ages?: string;
    outboundDepartureTime?: string;
    inboundDepartureTime?: string;
    outboundOfferId?: string;
  } & NavigationOptions) => {
    router[replaceHistory ? 'replace' : 'push']({
      pathname: outboundOfferId
        ? `/search/train/europe/${correlationId}/inbound`
        : `/search/train/europe/${correlationId}/outbound`,
      query: {
        category: RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET,
        journey,
        ...(origin?.code &&
          origin?.label &&
          origin?.type && {
            origin: `${origin.label}${TRAIN_PLACE_SPLIT_SYMBOL}${origin.type}${TRAIN_PLACE_SPLIT_SYMBOL}${origin.code}`,
          }),
        ...(destination?.code &&
          destination?.label &&
          destination?.type && {
            destination: `${destination.label}${TRAIN_PLACE_SPLIT_SYMBOL}${destination.type}${TRAIN_PLACE_SPLIT_SYMBOL}${destination.code}`,
          }),
        ...(departureDate && { departureDate }),
        ...(outboundDepartureTime && { outboundDepartureTime }),
        ...(inboundDepartureTime && { inboundDepartureTime }),
        ...(journey === JOURNEY_TYPE.ROUND_TRIP && arrivalDate && { arrivalDate }),
        ...(ages && { ages }),
        ...(outboundOfferId && { outboundOfferId }),
      },
    });
  };

  // 옵션 목록 페이지로 이동하는 함수
  const navigateToOfferList = ({
    correlationId,
    searchId,
    legSolutionId,
    journey,
    departureTime,
    arrivalTime,
    ages,
    outboundOfferId,
    inboundOfferId,
    selectedOfferId,
    origin: originCode,
    originType,
    originLabel,
    destination: destinationCode,
    destinationType,
    destinationLabel,
    replaceHistory = false,
  }: {
    correlationId: string;
    searchId: string;
    legSolutionId: string;
    journey: JourneyType;
    /** yyyy-MM-ddTHH:mm:ss: ex) '2025-05-01T00:00:00' */
    departureTime?: string;
    /** yyyy-MM-ddTHH:mm:ss: ex) '2025-05-01T00:00:00' */
    arrivalTime?: string;
    /** 숫자와 콤마가 포함된 문자열: ex) '2,5,20' */
    ages?: string;
    outboundOfferId?: string;
    inboundOfferId?: string;
    selectedOfferId?: string;
  } & SearchPlaceParams &
    NavigationOptions) => {
    router[replaceHistory ? 'replace' : 'push']({
      pathname: `/search/train/europe/${correlationId}/${searchId}/${legSolutionId}`,
      query: {
        journey,
        ...(originCode &&
          originType &&
          originLabel && {
            origin: `${originLabel}${TRAIN_PLACE_SPLIT_SYMBOL}${originType}${TRAIN_PLACE_SPLIT_SYMBOL}${originCode}`,
          }),
        ...(destinationCode &&
          destinationType &&
          destinationLabel && {
            destination: `${destinationLabel}${TRAIN_PLACE_SPLIT_SYMBOL}${destinationType}${TRAIN_PLACE_SPLIT_SYMBOL}${destinationCode}`,
          }),
        ...(departureTime && { departureDate: departureTime.split('T')[0] }),
        ...(arrivalTime && { arrivalDate: arrivalTime.split('T')[0] }),
        ...(ages && { ages }),
        ...(outboundOfferId && { outboundOfferId }),
        ...(inboundOfferId && { inboundOfferId }),
        ...(selectedOfferId && { selectedOfferId }),
      },
    });
  };

  // 티켓 상세 조회 페이지로 이동하는 함수
  const navigateToTicketDetail = ({
    correlationId,
    searchId,
    outboundOfferId,
    inboundOfferId,
    replaceHistory = false,
  }: {
    correlationId: string;
    searchId: string;
    outboundOfferId: string;
    inboundOfferId?: string;
  } & NavigationOptions) => {
    router[replaceHistory ? 'replace' : 'push']({
      pathname: inboundOfferId
        ? `/ticket/train/europe/${correlationId}/${searchId}/${outboundOfferId},${inboundOfferId}`
        : `/ticket/train/europe/${correlationId}/${searchId}/${outboundOfferId}`,
    });
  };

  // 여행자 정보 입력 페이지로 이동하는 함수
  const navigateToTravelerInfo = (trpOrderId: string) => {
    router.push({
      pathname: `/traveler/train/europe/${trpOrderId}`,
    });
  };

  return {
    navigateToSearch,
    navigateToSearchResult,
    navigateToOfferList,
    navigateToTicketDetail,
    navigateToTravelerInfo,
  };
};

export default useRailEuropeNavigation;
